import React from 'react';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarsIcon from '@mui/icons-material/Stars';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';

interface CreditsDisplayProps {
	variant?: 'chip' | 'text' | 'icon';
	size?: 'small' | 'medium';
	showRefresh?: boolean;
	onClick?: () => void;
}

const CreditsDisplay: React.FC<CreditsDisplayProps> = ({
	variant = 'chip',
	size = 'medium',
	showRefresh = false,
	onClick
}) => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const {
		totalCredits,
		initialCredits,
		paidCredits,
		loading,
		creditsLoading,
		loadCredits
	} = useCredits();

	const isLoading = loading || creditsLoading;

	const handleRefresh = async (e: React.MouseEvent) => {
		e.stopPropagation();
		await loadCredits();
	};

	const handleClick = () => {
		if (onClick) {
			onClick();
		} else {
			navigate('/pricing');
		}
	};

	if (!user) {
		return null;
	}

	if (totalCredits === null && !isLoading) {
		return null;
	}

	const displayCredits = totalCredits ?? 0;
	const creditsLabel = isLoading
		? 'Loading...'
		: `${displayCredits} credit${displayCredits !== 1 ? 's' : ''}`;
	const tooltipText = isLoading
		? 'Updating credits...'
		: displayCredits === 0 
		? 'No credits remaining. Click to get more credits.'
		: `${displayCredits} AI credit${displayCredits !== 1 ? 's' : ''} available${initialCredits > 0 ? ` (${initialCredits} free + ${paidCredits} paid)` : paidCredits > 0 ? ` (${paidCredits} paid)` : ''}`;

	if (variant === 'icon') {
		return (
			<Tooltip title={isLoading ? 'Updating credits...' : tooltipText}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<IconButton
						size="small"
						onClick={handleClick}
						sx={{ color: 'inherit' }}
					>
						<StarsIcon fontSize="small" />
					</IconButton>
					{isLoading ? (
						<CircularProgress size={16} sx={{ mx: 0.5 }} />
					) : (
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{displayCredits}
						</Typography>
					)}
					{showRefresh && (
						<IconButton
							size="small"
							onClick={handleRefresh}
							disabled={isLoading}
							sx={{ color: 'inherit', ml: 0.5 }}
						>
							<RefreshIcon fontSize="small" />
						</IconButton>
					)}
				</Box>
			</Tooltip>
		);
	}

	if (variant === 'text') {
		return (
			<Tooltip title={isLoading ? 'Updating credits...' : tooltipText}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Typography variant="body2" color="text.secondary">
						AI Credits:
					</Typography>
					{isLoading ? (
						<CircularProgress size={16} />
					) : (
						<Typography
							variant="body2"
							sx={{
								fontWeight: 700,
								color: displayCredits === 0 ? 'error.main' : displayCredits < 10 ? 'warning.main' : 'success.main'
							}}
						>
							{displayCredits}
						</Typography>
					)}
					{showRefresh && (
						<IconButton
							size="small"
							onClick={handleRefresh}
							disabled={isLoading}
						>
							<RefreshIcon fontSize="small" />
						</IconButton>
					)}
				</Box>
			</Tooltip>
		);
	}

	// Default: chip variant
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
			<Tooltip title={isLoading ? 'Updating credits...' : tooltipText}>
				<Chip
					icon={isLoading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <StarsIcon />}
					label={creditsLabel}
					size={size}
					color={isLoading ? 'default' : displayCredits === 0 ? 'error' : displayCredits < 10 ? 'warning' : 'success'}
					variant={displayCredits === 0 && !isLoading ? 'filled' : 'outlined'}
					onClick={handleClick}
					sx={{
						cursor: 'pointer',
						fontWeight: 600,
						'& .MuiChip-icon': {
							marginLeft: '8px'
						}
					}}
				/>
			</Tooltip>
			{showRefresh && (
				<IconButton
					size="small"
					onClick={handleRefresh}
					disabled={isLoading}
					sx={{ color: 'inherit' }}
				>
					<RefreshIcon fontSize="small" />
				</IconButton>
			)}
		</Box>
	);
};

export default CreditsDisplay;
